import { RouteParamsType } from '../../types/utils/router-params.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { createSlice } from '@reduxjs/toolkit';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import { StarlinkCircuitListItemType, StarlinkCircuitType } from '../../types/starlink/starlink.types';
import {
  deleteStarlinkCircuitDetails,
  getStarlinkCircuitDetails,
  getStarlinkCircuitsList,
  patchStarlinkCircuitDetails,
  postStarlinkCircuitDetails,
} from './starlink.action';

export interface StarlinkState {
  starlinkCircuitsRouteParams: RouteParamsType;
  starlinkCircuitsList: null | PaginationType<StarlinkCircuitListItemType>;
  starlinkCircuitsListFetchStatus: FETCH_STATUS;
  starlinkCircuitDetails: null | StarlinkCircuitType;
  starlinkCircuitDetailsFetchStatus: FETCH_STATUS;
  starlinkCircuitDetailsPostStatus: FETCH_STATUS;
  starlinkCircuitDetailsPatchStatus: FETCH_STATUS;
  starlinkCircuitDetailsDeleteStatus: FETCH_STATUS;
}

export const initialStarlinkCircuitsRouteParams = {
  page: 1,
  limit: 10,
  order: '-starlink_circuit.id',
  q: '',
};

const INIT_STATE: StarlinkState = {
  starlinkCircuitsRouteParams: initialStarlinkCircuitsRouteParams,
  starlinkCircuitsList: null,
  starlinkCircuitsListFetchStatus: FETCH_STATUS.NULL,
  starlinkCircuitDetails: null,
  starlinkCircuitDetailsFetchStatus: FETCH_STATUS.NULL,
  starlinkCircuitDetailsPostStatus: FETCH_STATUS.NULL,
  starlinkCircuitDetailsPatchStatus: FETCH_STATUS.NULL,
  starlinkCircuitDetailsDeleteStatus: FETCH_STATUS.NULL,
};

export const starlinkSlice = createSlice({
  name: 'starlink',
  initialState: INIT_STATE,
  reducers: {
    setStarlinkCircuitsList: (state, action) => {
      state.starlinkCircuitsList = action.payload;
    },
    setStarlinkCircuitsListFetchStatus: (state, action) => {
      state.starlinkCircuitsListFetchStatus = action.payload;
    },
    setStarlinkCircuitDetails: (state, action) => {
      state.starlinkCircuitDetails = action.payload;
    },
    setStarlinkCircuitDetailsFetchStatus: (state, action) => {
      state.starlinkCircuitDetailsFetchStatus = action.payload;
    },
    setStarlinkCircuitDetailsPostStatus: (state, action) => {
      state.starlinkCircuitDetailsPostStatus = action.payload;
    },
    setStarlinkCircuitDetailsPatchStatus: (state, action) => {
      state.starlinkCircuitDetailsPatchStatus = action.payload;
    },
    setStarlinkCircuitDetailsDeleteStatus: (state, action) => {
      state.starlinkCircuitDetailsDeleteStatus = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'starlinkCircuits') {
        state.starlinkCircuitsRouteParams = { ...state.starlinkCircuitsRouteParams, ...payload.data };
      }
    });

    // CREATE FIBEER CIRCUIT DETAILS
    builder.addCase(postStarlinkCircuitDetails.pending, state => {
      state.starlinkCircuitDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postStarlinkCircuitDetails.rejected, (state, action) => {
      state.starlinkCircuitDetailsPostStatus = FETCH_STATUS.REJECTED;
      snackbarUtils.error(action.payload as string);
    });
    builder.addCase(postStarlinkCircuitDetails.fulfilled, state => {
      state.starlinkCircuitDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Starlink Circuit created successfully');
      });
    });

    // GET FIBEER CIRCUIT LIST
    builder.addCase(getStarlinkCircuitsList.pending, state => {
      state.starlinkCircuitsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getStarlinkCircuitsList.rejected, (state, action) => {
      if (action.payload) {
        state.starlinkCircuitsListFetchStatus = FETCH_STATUS.REJECTED;
        // state.starlinkCircuitsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getStarlinkCircuitsList.fulfilled, (state, action) => {
      state.starlinkCircuitsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.starlinkCircuitsList = action.payload;
    });

    // GET FIBEER CIRCUIT DETAILS
    builder.addCase(getStarlinkCircuitDetails.pending, state => {
      state.starlinkCircuitDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getStarlinkCircuitDetails.rejected, (state, action) => {
      state.starlinkCircuitDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.starlinkCircuitDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getStarlinkCircuitDetails.fulfilled, (state, action) => {
      state.starlinkCircuitDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.starlinkCircuitDetails = action.payload;
    });

    // PATCH FIBEER CIRCUIT DETAILS
    builder.addCase(patchStarlinkCircuitDetails.pending, state => {
      state.starlinkCircuitDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchStarlinkCircuitDetails.rejected, (state, action) => {
      state.starlinkCircuitDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchStarlinkCircuitDetails.fulfilled, state => {
      state.starlinkCircuitDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Starlink Circuit updated successfully');
      });
    });

    // DELETE FIBEER CIRCUIT DETAILS
    builder.addCase(deleteStarlinkCircuitDetails.pending, state => {
      state.starlinkCircuitDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteStarlinkCircuitDetails.rejected, (state, action) => {
      state.starlinkCircuitDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteStarlinkCircuitDetails.fulfilled, state => {
      state.starlinkCircuitDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Starlink Circuit deleted successfully');
      });
    });
  },
});

export const {
  setStarlinkCircuitDetailsDeleteStatus,
  setStarlinkCircuitDetailsFetchStatus,
  setStarlinkCircuitDetailsPatchStatus,
  setStarlinkCircuitDetailsPostStatus,
  setStarlinkCircuitsListFetchStatus,
  setStarlinkCircuitDetails,
  setStarlinkCircuitsList,
} = starlinkSlice.actions;

export default starlinkSlice.reducer;
